import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SeoMeta from '../components/SeoMeta';
import { ObfuscatedEmail } from '../components/ObfuscatedEmail';

export default function CookiesPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <SeoMeta
        title="Politique de Cookies"
        description="Politique de cookies Drone Maps : cookies essentiels, analytiques (Google Analytics) et de préférences. Gérez vos préférences en un clic."
        keywords="cookies Drone Maps, RGPD cookies, Google Analytics drone, consent mode v2, gestion cookies"
      />
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 mb-8">
          <ArrowLeft className="w-4 h-4" />
          Retour à l'accueil
        </Link>
        
        <h1 className="text-3xl font-bold text-slate-900 mb-8 font-[Barlow]">Politique de Cookies</h1>
        
        <div className="bg-white rounded-xl p-8 shadow-sm space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">1. Qu'est-ce qu'un cookie ?</h2>
            <p className="text-slate-600 leading-relaxed">
              Un cookie est un petit fichier texte déposé sur votre terminal (ordinateur, tablette, smartphone) 
              lors de votre visite sur un site web. Il permet au site de mémoriser des informations sur votre 
              visite, comme vos préférences de langue et d'autres paramètres.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">2. Les cookies que nous utilisons</h2>
            
            <div className="space-y-6">
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <h3 className="font-semibold text-emerald-800 mb-2">🔒 Cookies essentiels (obligatoires)</h3>
                <p className="text-slate-600 text-sm mb-2">
                  Ces cookies sont nécessaires au fonctionnement du site et ne peuvent pas être désactivés.
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500">
                      <th className="pb-2">Cookie</th>
                      <th className="pb-2">Finalité</th>
                      <th className="pb-2">Durée</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-600">
                    <tr>
                      <td className="py-1 font-mono text-xs">dronemaps_token</td>
                      <td className="py-1">Authentification utilisateur</td>
                      <td className="py-1">24 heures</td>
                    </tr>
                    <tr>
                      <td className="py-1 font-mono text-xs">cookie_consent</td>
                      <td className="py-1">Mémoriser votre choix de cookies</td>
                      <td className="py-1">12 mois</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="p-4 bg-sky-50 rounded-lg border border-sky-200">
                <h3 className="font-semibold text-sky-800 mb-2">📊 Cookies analytiques (optionnels)</h3>
                <p className="text-slate-600 text-sm mb-2">
                  Ces cookies nous permettent de comprendre comment vous utilisez le site pour l'améliorer.
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500">
                      <th className="pb-2">Cookie</th>
                      <th className="pb-2">Finalité</th>
                      <th className="pb-2">Durée</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-600">
                    <tr>
                      <td className="py-1 font-mono text-xs">_ga</td>
                      <td className="py-1">Google Analytics - Identifier les visiteurs</td>
                      <td className="py-1">13 mois</td>
                    </tr>
                    <tr>
                      <td className="py-1 font-mono text-xs">_ga_*</td>
                      <td className="py-1">Google Analytics - Conserver l'état de session</td>
                      <td className="py-1">13 mois</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h3 className="font-semibold text-amber-800 mb-2">🎯 Cookies de préférences (optionnels)</h3>
                <p className="text-slate-600 text-sm mb-2">
                  Ces cookies permettent de mémoriser vos préférences pour améliorer votre expérience.
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500">
                      <th className="pb-2">Cookie</th>
                      <th className="pb-2">Finalité</th>
                      <th className="pb-2">Durée</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-600">
                    <tr>
                      <td className="py-1 font-mono text-xs">i18nextLng</td>
                      <td className="py-1">Préférence de langue d'affichage</td>
                      <td className="py-1">Persistant</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">3. Gérer vos préférences</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Vous pouvez à tout moment modifier vos préférences de cookies :
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>Via la bannière de cookies qui apparaît lors de votre première visite</li>
              <li>Via les paramètres de votre navigateur</li>
              <li>En cliquant sur le bouton ci-dessous pour réinitialiser vos préférences</li>
            </ul>
            <button 
              onClick={() => {
                localStorage.removeItem('cookie_consent');
                window.location.reload();
              }}
              className="mt-4 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors"
            >
              Réinitialiser mes préférences de cookies
            </button>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">4. Paramétrage du navigateur</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Vous pouvez également configurer votre navigateur pour accepter ou refuser les cookies :
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline">Google Chrome</a></li>
              <li><a href="https://support.mozilla.org/fr/kb/activer-desactiver-cookies" target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline">Mozilla Firefox</a></li>
              <li><a href="https://support.apple.com/fr-fr/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline">Safari</a></li>
              <li><a href="https://support.microsoft.com/fr-fr/microsoft-edge/supprimer-les-cookies-dans-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline">Microsoft Edge</a></li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">5. Contact</h2>
            <p className="text-slate-600 leading-relaxed">
              Pour toute question concernant notre utilisation des cookies, contactez-nous à : 
              <ObfuscatedEmail linkClassName="text-sky-600 hover:underline ml-1" />
            </p>
          </section>

          <p className="text-sm text-slate-400 pt-4 border-t border-slate-200">
            Dernière mise à jour : Février 2026
          </p>
        </div>
      </div>
    </div>
  );
}
